import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import type {
  AuthScheme,
  IntegrationBusinessUnit,
  IntegrationValidationError,
  Webhook
} from 'api';
import {
  getGetIntegrationWebhooksQueryKey,
  usePatchIntegrationWebhooksUuid
} from 'api';
import type { EditWebhookFormValues } from 'validators/editWebhookForm';
import { editWebhookFormSchema } from 'validators/editWebhookForm';

import { useOrganisationId } from 'hooks/context';

import { useAuthenticationKeys } from '../../hooks/useAuthenticationKeys';
import { useBusinessUnits } from '../../hooks/useBusinessUnits';
import { extractIntegrationValidationError } from '../../utils/extractIntegrationValidationError';
import { EDIT_WEBHOOK_GENERIC_ERROR, EDIT_WEBHOOK_SUCCESS } from '../constants';

type UseEditWebhookFormArgs = {
  webhook: Webhook;
  onSaved: () => void;
};

export type UseEditWebhookForm = {
  form: UseFormReturn<EditWebhookFormValues>;
  businessUnits: IntegrationBusinessUnit[];
  authKeys: AuthScheme[];
  authSchemaDisabled: boolean;
  effectiveStatus: boolean;
  submit: ReturnType<UseFormReturn<EditWebhookFormValues>['handleSubmit']>;
  isSaving: boolean;
};

export const useEditWebhookForm = ({
  webhook,
  onSaved
}: UseEditWebhookFormArgs): UseEditWebhookForm => {
  const organisationId = useOrganisationId<number>();
  const queryClient = useQueryClient();
  const openNotification = useOpenNotification();
  const updateMutation = usePatchIntegrationWebhooksUuid();

  const { keys: authKeys } = useAuthenticationKeys();
  const { businessUnits } = useBusinessUnits();

  const form = useForm<EditWebhookFormValues>({
    mode: 'onChange',
    resolver: yupResolver(editWebhookFormSchema),
    defaultValues: {
      endpointUrl: webhook.destination_url ?? '',
      businessUnitUuid: webhook.business_unit_uuid,
      xAuthUuid: webhook.auth_uuid ?? undefined,
      status: webhook.active
    }
  });

  const status = form.watch('status');
  const xAuthUuid = form.watch('xAuthUuid');
  const authSchemaDisabled = !authKeys.find((key) => key.uuid === xAuthUuid)
    ?.enabled;
  const effectiveStatus = authSchemaDisabled ? false : status;

  const handleSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: getGetIntegrationWebhooksQueryKey(organisationId)
    });
    openNotification({ kind: 'success', message: EDIT_WEBHOOK_SUCCESS });
    onSaved();
  };

  const handleError = (error: IntegrationValidationError | null) => {
    openNotification({
      kind: 'error',
      message: extractIntegrationValidationError(
        error,
        EDIT_WEBHOOK_GENERIC_ERROR
      )
    });
  };

  const submit = form.handleSubmit((values) =>
    updateMutation.mutate(
      {
        organisationId,
        uuid: webhook.uuid,
        data: {
          destination_url: values.endpointUrl.trim(),
          auth_uuid: values.xAuthUuid,
          active: effectiveStatus,
          business_unit_uuid: values.businessUnitUuid
        }
      },
      { onSuccess: handleSuccess, onError: handleError }
    )
  );

  return {
    form,
    businessUnits,
    authKeys,
    authSchemaDisabled,
    effectiveStatus,
    submit,
    isSaving: updateMutation.isPending
  };
};
